// Copyright (c) Ping. All rights reserved.
//
// In-call ephemeral data-channel protocol. Reactions, hand-raise, in-call chat
// (Call Thread), and notes edits ride LiveKit's reliable data channel rather
// than MLS AppEvents — sub-50ms fan-out with no ratchet/epoch cost, the right
// transport for high-cadence in-call signals (calls.md §11 "call-ephemeral").
// Durable call lifecycle (invite/accept/decline/end) still rides MLS.
//
// Frames are tiny JSON; the call is already E2EE at the media layer and the
// data channel is encrypted in transit (DTLS). For v1 we do not double-encrypt
// these ephemeral frames — documented in calls-drift.md.

/** Full-screen celebratory effects (the reaction menu's "Reactions" tab). */
export type CallEffectKind = 'confetti' | 'hearts' | 'fireworks';

export type CallDataFrame =
  | { readonly t: 'reaction'; readonly emoji: string; readonly from: string; readonly name: string }
  | { readonly t: 'hand'; readonly raised: boolean; readonly from: string; readonly name: string }
  // Persistent "be right back / away" toggle — stays on the participant card
  // until cleared (unlike the transient `reaction`).
  | { readonly t: 'brb'; readonly active: boolean; readonly from: string; readonly name: string }
  // Full-screen animated effect streamed to everyone, labelled with the sender.
  | {
      readonly t: 'effect';
      readonly effect: CallEffectKind;
      readonly from: string;
      readonly name: string;
    }
  | {
      readonly t: 'chat';
      readonly id: string;
      readonly text: string;
      readonly from: string;
      readonly name: string;
      readonly at: number;
    }
  | { readonly t: 'chat_react'; readonly msgId: string; readonly emoji: string; readonly from: string }
  | { readonly t: 'notes'; readonly rev: number; readonly doc: unknown; readonly from: string }
  | { readonly t: 'draw'; readonly stroke: DrawStroke; readonly from: string }
  // In-progress stroke broadcast live (throttled) while the pointer is down, so
  // remote viewers see the line draw in real time; superseded by the final
  // `draw` frame (same `id`) on pointer-up. `points` is INCREMENTAL — only the
  // points added since the previous frame for this `id`; the receiver appends
  // them. (Keeps a long scribble O(n) total, not O(n²) — see DrawingCanvas.)
  | {
      readonly t: 'draw_live';
      readonly id: string;
      readonly color: string;
      readonly points: number[];
      readonly from: string;
    }
  | { readonly t: 'draw_clear'; readonly from: string }
  | { readonly t: 'recording'; readonly active: boolean; readonly from: string; readonly name: string };

/** One annotation stroke. `points` are normalized [x0,y0,x1,y1,…] in 0..1 so
 *  they map across every viewer's video size. */
export interface DrawStroke {
  readonly id: string;
  readonly color: string;
  readonly width: number;
  readonly points: number[];
  readonly from: string;
}

const enc = new TextEncoder();
const dec = new TextDecoder();

export function encodeCallData(frame: CallDataFrame): Uint8Array {
  return enc.encode(JSON.stringify(frame));
}

export function decodeCallData(payload: Uint8Array): CallDataFrame | null {
  try {
    const obj = JSON.parse(dec.decode(payload)) as { t?: unknown };
    if (typeof obj.t !== 'string') return null;
    return obj as CallDataFrame;
  } catch {
    return null;
  }
}
